import React from 'react';
import FieldReportHeader from './FieldReportHeader';
import { MapPin, Check, X, Minus, Clock, CheckCircle, XCircle, Ruler, Camera } from 'lucide-react';
import { resolveFileUrl, resolveSignatureUrl } from '../../lib/fileUtils';
import { SafeImage } from '../SafeImage';
import { displayValue } from '../../lib/displayValue';

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'bg-green-100 text-green-700';
    case 'submitted_pending_review': return 'bg-blue-100 text-blue-700';
    case 'draft': return 'bg-slate-100 text-slate-700';
    default: return 'bg-slate-100 text-slate-700';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'completed': return 'Completed';
    case 'submitted_pending_review': return 'Pending Review';
    case 'draft': return 'Draft';
    default: return status || 'Draft';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr).trim());
    const date = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true 
    });
  } catch {
    return dateStr;
  }
};

// Photo status icon
const PhotoStatus = ({ status }) => {
  if (status === 'pass') return <Check className="w-4 h-4 text-green-600" />;
  if (status === 'fail') return <X className="w-4 h-4 text-red-600" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

export default function SoftDigDocument({ report, printRef }) {
  // Build the list of digs to render. Use the multi-dig array if present;
  // otherwise fall back to a single synthetic dig from the flat legacy fields.
  const digs = (Array.isArray(report.soft_digs) && report.soft_digs.length > 0)
    ? report.soft_digs
    : [{
        soft_dig_number: report.soft_dig_number,
        utility_type: report.utility_type,
        utility_owner: report.utility_owner,
        utility_size_inches: report.utility_size_inches,
        utility_material: report.utility_material,
        utility_color_code: report.utility_color_code,
        portion_exposed: report.portion_exposed,
        observed_condition: report.observed_condition,
        surface_covering: report.surface_covering,
        soil_condition: report.soil_condition,
        core_thickness_inches: report.core_thickness_inches,
        elevation_from_grade_inches: report.elevation_from_grade_inches,
        elevation_to_bottom_inches: report.elevation_to_bottom_inches,
        site_plan_photo_url: report.site_plan_photo_url,
        site_plan_photo_status: report.site_plan_photo_status,
        exposed_utility_photo_url: report.exposed_utility_photo_url,
        exposed_utility_photo_status: report.exposed_utility_photo_status,
        measurement_photo_url: report.measurement_photo_url,
        measurement_photo_status: report.measurement_photo_status,
        additional_photos: report.additional_photos,
        reference_points: report.reference_points,
        note: report.note,
      }];

  return (
    <div 
      ref={printRef} 
      id="soft-dig-document" 
      className="bg-white w-full max-w-4xl shadow-xl rounded-lg overflow-hidden print:shadow-none print:rounded-none print:max-w-none" 
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {/* Feb-2026: top-right pill shows the JOB number (matches PreCon).
          The per-hole `soft_dig_number` used to sit here but Lauren
          reported it was confusing — clients recognize the job, not
          the hole reference. See PreConDocument.js line ~61. */}
      <FieldReportHeader reportType="Soft Dig Report" reportNumber={report.job_number || report.id?.slice(0, 8)} />
      
      <div className="px-6 py-4">
        {/* Title & Status
            Feb-2026: added a "Soft Dig" identifying pill next to
            the status, matching LocateDocument's "Utility Locate"
            layout (see LocateDocument.js:84-93). Same amber styling
            as Locate — soft-dig-report has the same "amber accent"
            visual language throughout. */}
        <div className="mb-4 text-center">
          <h2 className="text-lg font-semibold text-slate-900">Soft Dig / Pothole Report</h2>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(report.status)}`}>
              {getStatusLabel(report.status)}
            </span>
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
              Soft Dig
            </span>
          </div>
          {digs.length > 1 && (
            <p className="text-[11px] text-slate-500 mt-1">{digs.length} Soft Digs Documented</p>
          )}
        </div>

        {/* Job & Site Info */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Job Information</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-[10px]">Job Number</p>
              <p className="font-medium text-amber-600">{report.job_number || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Date</p>
              <p className="font-medium text-slate-700">{formatDate(report.date)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Technician</p>
              <p className="font-medium text-slate-700">{report.cll_lead_on_site || '—'}</p>
            </div>
          </div>
          <div className="mt-3">
            <p className="text-slate-500 text-[10px]">Site Location</p>
            <p className="font-medium text-slate-700 flex items-center gap-1">
              <MapPin className="w-3 h-3 text-slate-400" />
              {report.site_location || '—'}
            </p>
          </div>
        </div>

        {/* Project Details — Feb-2026: added to match PreCon so clients
            see Project Name / Client / Project Number / Scope on the PDF.
            Fields joined server-side from the linked job in
            GET /api/soft-digs/{id}. Kept blue-tinted, identical layout
            to PreConDocument.js so downstream PDF renderers stay in sync. */}
        {(report.project_name || report.client_owner || report.project_number || report.scope_overview) && (
        <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-100">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-600 mb-2">Project Details</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-blue-500 text-[10px]">Project Name</p>
              <p className="font-medium text-slate-700">{report.project_name || '—'}</p>
            </div>
            <div>
              <p className="text-blue-500 text-[10px]">Client/Owner</p>
              <p className="font-medium text-slate-700">{report.client_owner || '—'}</p>
            </div>
            <div>
              <p className="text-blue-500 text-[10px]">Project Number</p>
              <p className="font-medium text-slate-700">{report.project_number || '—'}</p>
            </div>
          </div>
          {report.scope_overview && (
            <div className="mt-3">
              <p className="text-blue-500 text-[10px]">Scope Overview</p>
              <p className="text-sm text-slate-700">{report.scope_overview}</p>
            </div>
          )}
        </div>
        )}

        {digs.map((dig, digIdx) => (
          <div key={digIdx} className="mb-6 pb-4 border-b border-slate-200 last:border-b-0">
            {digs.length > 1 && (
              <div className="mb-3 flex items-center gap-2">
                <span className="inline-block px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold">
                  Soft Dig #{digIdx + 1}
                </span>
                {dig.soft_dig_number && (
                  <span className="text-xs text-slate-500">({dig.soft_dig_number})</span>
                )}
              </div>
            )}

        {/* Utility Information */}
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Utility Information</p>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100">
                <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Type</th>
                <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Owner</th>
                <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Size</th>
                <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Material</th>
                <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Color</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="py-1.5 px-2 text-slate-700 text-[11px]">{displayValue(dig.utility_type)}</td>
                <td className="py-1.5 px-2 text-slate-700 text-[11px]">{dig.utility_owner || '—'}</td>
                <td className="py-1.5 px-2 text-slate-700 text-[11px]">{dig.utility_size_inches ? `${dig.utility_size_inches}"` : '—'}</td>
                <td className="py-1.5 px-2 text-slate-700 text-[11px]">{displayValue(dig.utility_material)}</td>
                <td className="py-1.5 px-2 text-slate-700 text-[11px]">{displayValue(dig.utility_color_code)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Site Conditions */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Site Conditions</p>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-[10px]">Portion Exposed</p>
              <p className="font-medium text-slate-700">{displayValue(dig.portion_exposed)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Condition</p>
              <p className="font-medium text-slate-700">{displayValue(dig.observed_condition)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Surface</p>
              <p className="font-medium text-slate-700">{displayValue(dig.surface_covering)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Soil</p>
              <p className="font-medium text-slate-700">{displayValue(dig.soil_condition)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Core Thickness</p>
              <p className="font-medium text-slate-700">
                {dig.core_thickness_inches ? `${dig.core_thickness_inches}"` : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Elevation Data */}
        <div className="mb-4 p-3 bg-amber-50 rounded border border-amber-200">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1">
            <Ruler className="w-3 h-3" /> Elevation Measurements
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-amber-600 text-[10px]">From Grade (Top of Utility)</p>
              <p className="text-xl font-bold text-amber-700">
                {dig.elevation_from_grade_inches ? `${dig.elevation_from_grade_inches}"` : '—'}
              </p>
            </div>
            <div>
              <p className="text-amber-600 text-[10px]">To Bottom of Utility</p>
              <p className="text-xl font-bold text-amber-700">
                {dig.elevation_to_bottom_inches ? `${dig.elevation_to_bottom_inches}"` : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Photos Section — Feb-2026: sized to match Locate/PreCon
            (grid-cols-2 h-72). Field crews reported the previous
            grid-cols-3 h-28 layout made client-review photos too small
            to read on the printed PDF.
            Oct-2026: moved ahead of Reference Points per client PDF
            reading-order request — Photos now render immediately after
            Elevation Measurements, Reference Points right after. */}
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
            <Camera className="w-3 h-3" /> Documentation Photos
          </p>
          <div className="grid grid-cols-2 gap-4">
            {dig.site_plan_photo_url && (
              <div className="relative break-inside-avoid">
                <SafeImage src={dig.site_plan_photo_url} alt="Site Plan" className="w-full h-72 object-cover rounded border" loading="lazy" decoding="async" />
                <div className="absolute top-2 right-2 bg-white rounded-full p-0.5">
                  <PhotoStatus status={dig.site_plan_photo_status} />
                </div>
                <p className="text-sm text-slate-700 mt-2 font-medium">Site Plan Photo</p>
              </div>
            )}
            {dig.exposed_utility_photo_url && (
              <div className="relative break-inside-avoid">
                <SafeImage src={dig.exposed_utility_photo_url} alt="Exposed Utility" className="w-full h-72 object-cover rounded border" loading="lazy" decoding="async" />
                <div className="absolute top-2 right-2 bg-white rounded-full p-0.5">
                  <PhotoStatus status={dig.exposed_utility_photo_status} />
                </div>
                <p className="text-sm text-slate-700 mt-2 font-medium">Exposed Utility</p>
              </div>
            )}
            {dig.measurement_photo_url && (
              <div className="relative break-inside-avoid">
                <SafeImage src={dig.measurement_photo_url} alt="Measurement" className="w-full h-72 object-cover rounded border" loading="lazy" decoding="async" />
                <div className="absolute top-2 right-2 bg-white rounded-full p-0.5">
                  <PhotoStatus status={dig.measurement_photo_status} />
                </div>
                <p className="text-sm text-slate-700 mt-2 font-medium">Measurement</p>
              </div>
            )}
          </div>
          {/* Additional photos if any — same size as the primary trio. */}
          {dig.additional_photos && dig.additional_photos.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              {dig.additional_photos.map((photo, idx) => (
                <div key={idx} className="break-inside-avoid">
                  <SafeImage src={resolveFileUrl(photo, 'preview')} alt={photo.caption || `Photo ${idx + 1}`} className="w-full h-72 object-cover rounded border" loading="lazy" decoding="async" />
                  {photo.caption && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words">{photo.caption}</p>}
                </div>
              ))}
            </div>
          )}
          {!dig.site_plan_photo_url && !dig.exposed_utility_photo_url && !dig.measurement_photo_url && (!dig.additional_photos || dig.additional_photos.length === 0) && (
            <p className="text-sm text-slate-400 italic">No photos attached</p>
          )}
        </div>

        {/* Reference Points Section
            Feb-2026: photos moved from a `w-16 h-16` sidebar into a
            full-width `grid-cols-2 h-72` block below the details —
            matches the Documentation Photos + Locate/PreCon sizing
            the client asked for. Details block still sits on top so
            the RP context (location/street/distance) reads first. */}
        {dig.reference_points && dig.reference_points.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Reference Points</p>
            <div className="space-y-3">
              {dig.reference_points.map((refPoint, idx) => (
                <div key={idx} className="p-3 bg-blue-50 rounded border border-blue-200 break-inside-avoid">
                  <div>
                    <p className="text-[10px] font-semibold text-blue-600 mb-1">Reference Point #{idx + 1}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-blue-500 text-[10px]">Location Description</p>
                        <p className="font-medium text-slate-700">{refPoint.location_desc || '—'}</p>
                      </div>
                      <div>
                        <p className="text-blue-500 text-[10px]">Street & Cross</p>
                        <p className="font-medium text-slate-700">{refPoint.street_cross || '—'}</p>
                      </div>
                      <div>
                        <p className="text-blue-500 text-[10px]">Distance</p>
                        <p className="font-medium text-slate-700">
                          {refPoint.distance_feet ? `${refPoint.distance_feet}' ` : ''}
                          {refPoint.distance_inches ? `${refPoint.distance_inches}"` : ''}
                          {!refPoint.distance_feet && !refPoint.distance_inches && '—'}
                        </p>
                      </div>
                      {refPoint.notes && (
                        <div>
                          <p className="text-blue-500 text-[10px]">Notes</p>
                          <p className="text-sm text-slate-600">{refPoint.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {(() => {
                    // Render attachments[] if present, else fall back to
                    // the legacy single photo_url for older records. PDFs /
                    // docs render as a small text link.
                    const atts = Array.isArray(refPoint.attachments) && refPoint.attachments.length > 0
                      ? refPoint.attachments
                      : (refPoint.photo_url ? [{ url: refPoint.photo_url, kind: 'image' }] : []);
                    if (atts.length === 0) return null;
                    return (
                      <div className="mt-3 grid grid-cols-2 gap-4">
                        {atts.map((a, ai) => {
                          const isFile = a.kind === 'file' || /\.(pdf|docx?|xlsx?)(\?|$)/i.test(a.url || '');
                          if (isFile) {
                            return (
                              <a
                                key={ai}
                                href={resolveFileUrl(a.url)}
                                target="_blank"
                                rel="noreferrer"
                                className="w-full h-72 flex flex-col items-center justify-center rounded border border-blue-200 bg-blue-50 text-blue-700 text-sm hover:bg-blue-100"
                              >
                                <span className="font-semibold">PDF</span>
                                <span className="truncate w-full px-2 text-center">{a.name || 'File'}</span>
                              </a>
                            );
                          }
                          return (
                            <SafeImage
                              key={ai}
                              src={resolveFileUrl(a.url, 'preview')}
                              alt={`Reference Point ${idx + 1} #${ai + 1}`}
                              className="w-full h-72 object-cover rounded border border-blue-200 break-inside-avoid"
                              loading="lazy"
                              decoding="async"
                            />
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {dig.note && (
          <div className="mb-4 p-3 bg-slate-50 rounded">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Notes</p>
            <p className="text-sm text-slate-600">{dig.note}</p>
          </div>
        )}
          </div>
        ))}

        {/* Signatures Section */}
        <div className="mb-4 border-t border-slate-200 pt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Signatures & Authorization</p>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Submitter Signature */}
            <div className="p-3 bg-slate-50 rounded">
              <p className="text-[10px] font-semibold text-slate-500 mb-2">Submitter / Prepared By</p>
              {report.submitter_signature_url ? (
                <div>
                  <img 
                    src={resolveSignatureUrl(report, 'submitter_signature')} 
                    alt="Submitter Signature" 
                    className="max-h-24 mb-2 border border-slate-200 rounded bg-white p-1" 
                  />
                  <p className="text-sm font-medium text-slate-700">{report.submitter_name || report.submitted_by || '—'}</p>
                  <p className="text-[10px] text-slate-500">{formatDateTime(report.submitter_signed_at)}</p>
                </div>
              ) : (
                <div>
                  <div className="border-b-2 border-slate-300 h-12 mb-1 flex items-end">
                    <span className="text-slate-300 text-sm italic pb-1">Not signed</span>
                  </div>
                  <p className="text-[10px] text-slate-500">Technician Signature & Date</p>
                </div>
              )}
            </div>

            {/* Reviewer/Approver Signature */}
            <div className={`p-3 rounded ${report.status === 'completed' ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}>
              <p className={`text-[10px] font-semibold mb-2 ${report.status === 'completed' ? 'text-green-600' : 'text-slate-500'}`}>
                Manager Approval
              </p>
              {report.reviewer_signature_url ? (
                <div>
                  <img 
                    src={resolveSignatureUrl(report, 'reviewer_signature')} 
                    alt="Reviewer Signature" 
                    className="max-h-24 mb-2 border border-green-200 rounded bg-white p-1" 
                  />
                  <p className="text-sm font-medium text-green-700">{report.reviewer_name || report.reviewed_by || '—'}</p>
                  <p className="text-[10px] text-green-600">{formatDateTime(report.reviewer_signed_at || report.reviewed_at)}</p>
                </div>
              ) : (
                <div>
                  <div className="border-b-2 border-slate-300 h-12 mb-1 flex items-end">
                    <span className="text-slate-300 text-sm italic pb-1">
                      {report.status === 'submitted_pending_review' ? 'Awaiting approval' : 'Not signed'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">Manager Signature & Date</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status History */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Report Status</p>
          <div className="flex flex-wrap gap-4 text-[11px]">
            {report.created_at && (
              <div className="flex items-center gap-1 text-slate-600">
                <Clock className="w-3 h-3" />
                <span>Created: {formatDateTime(report.created_at)}</span>
              </div>
            )}
            {report.submitted_at && (
              <div className="flex items-center gap-1 text-blue-600">
                <CheckCircle className="w-3 h-3" />
                <span>Submitted: {formatDateTime(report.submitted_at)}</span>
              </div>
            )}
            {report.reviewed_at && report.status === 'completed' && (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-3 h-3" />
                <span>Approved: {formatDateTime(report.reviewed_at)}</span>
              </div>
            )}
            {report.rejected_at && (
              <div className="flex items-center gap-1 text-red-600">
                <XCircle className="w-3 h-3" />
                <span>Rejected: {formatDateTime(report.rejected_at)}</span>
              </div>
            )}
          </div>
          {report.rejection_reason && (
            <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700">
              <span className="font-semibold">Rejection Reason:</span> {report.rejection_reason}
            </div>
          )}
        </div>
      </div>
      
      {/* Legal Disclaimer */}
      <div className="px-6 py-3 bg-slate-100 border-t border-slate-200">
        <p className="text-[9px] text-slate-500 text-center italic">
          This report documents field observations at the time of service. 
          Utility locations are approximate unless physically exposed. Measurements are from existing grade.
        </p>
      </div>
      
      {/* Footer bar */}
      <div className="px-6 py-3 bg-slate-800 flex items-center justify-between text-[10px] text-slate-300">
        <span>Center Line Locating, LLC · Charlotte, NC · (704) 315-5820</span>
        {/* Feb-2026: match the top-right header (BUG 4 round 2) —
            job number, not the per-hole `soft_dig_number` (TH-1 etc.). */}
        <span>Soft Dig Report #{report.job_number || report.id?.slice(0, 8)}</span>
      </div>
    </div>
  );
}
